// ProfilePhoto.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useOnline } from 'context/OnlineContext';
import { useTheme } from 'context/ThemeContext';
import { useBase } from 'context/BaseContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Utilities
import { PhotoShape } from 'utilities/Enums';
import { buildBaseMemberProfilePath } from 'utilities/Routing';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// CSS
import './ProfilePhoto.css';

/**
 * Builds avatar initials from a display name.
 * One word → first letter; two+ words → first letters of the first two words.
 *
 * @param {object|null|undefined} node
 * @returns {string}
 */
const getProfilePhotoInitials = (node) => {
    const label = String(
        node?.name
        || node?.title
        || node?.user?.name
        || node?.data_profile?.name
        || '',
    ).trim();

    if (!label) return '?';

    const words = label.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
        const first = words[0].charAt(0);
        const second = words[1].charAt(0);
        return `${first}${second}`.toUpperCase();
    }

    return words[0].charAt(0).toUpperCase() || '?';
};

/**
 * ProfilePhoto
 *
 * This component renders an icon or letter representing a user.
 *
 * @param {object} node - The USER node to represent.
 * @param {number} size - The size of the photo in pixels. Default is 40.
 * @param {number} borderWidth - The width of the border in pixels. Default is 2.
 * @param {string} borderColor - Optional border color override.
 * @param {string|number} borderRadius - The border radius. Default is 7px.
 * @param {boolean} allowNavigation - Whether clicking should navigate to the member profile route.
 * @param {function} onClick - Click handler for the photo.
 * @param {string} shape - The shape type for the photo.
 *
 * @returns {ReactNode} The rendered component.
 */
const ProfilePhoto = React.memo(({
    node,
    size = 40,
    borderWidth = 1,
    borderColor,
    borderRadius = '5px',
    allowNavigation = false,
    onClick,
    shape = PhotoShape.CIRCLE
}) => {

    // ----- CONTEXT ----- //

    // Online Context

    const { isUserOnline } = useOnline();

    // Theme Context
    const { theme } = useTheme();

    // Base Context
    const { activeBase } = useBase();

    // ----- ROUTING ----- //

    // Navigation

    const navigate = useNavigate();

    // ----- STATE ----- //

    // Resolved Cover URL State

    const [resolvedCoverUrl, setResolvedCoverUrl] = useState(null);

    // Image Failure State
    const [imageFailed, setImageFailed] = useState(false);

    // ----- MEMOS ----- //

    // Memoized user key used for online state lookup.

    const userKey = useMemo(() => {
        return String(node?.userKey || node?.key || '').trim();
    }, [node?.key, node?.userKey]);

    const baseKey = useMemo(() => {
        return String(node?.baseKey || activeBase?.key || '').trim();
    }, [activeBase?.key, node?.baseKey]);

    const canNavigateToProfile = Boolean(allowNavigation && userKey && baseKey);

    const rawCoverUrl = typeof node?.coverUrl === 'string' ? node.coverUrl.trim() : '';
    const canRenderRawCoverUrl = (
        rawCoverUrl.startsWith('blob:')
        || rawCoverUrl.startsWith('data:')
        || rawCoverUrl.includes('://')
    );

    const showOnlineBorder = Boolean(userKey) && isUserOnline(userKey);
    //const effectiveBorderWidth = showOnlineBorder ? 1 : borderWidth;
    const effectiveBorderWidth = borderWidth;
    const effectiveBorderColor = showOnlineBorder ? theme.active : borderColor;
    const radius = shape === PhotoShape.CIRCLE ? '50%' : borderRadius;
    const imageSrc = resolvedCoverUrl || (canRenderRawCoverUrl ? rawCoverUrl : null);
    const showImage = Boolean(imageSrc) && !imageFailed;

    const initials = useMemo(
        () => getProfilePhotoInitials(node),
        [node?.data_profile?.name, node?.name, node?.title, node?.user?.name],
    );
    // Keep one- and two-letter initials at the same scale so stacks look even.
    const initialFontSize = size * 0.42;

    // ----- EFFECTS ----- //

    // Resolves an image URL from direct URL values or storage paths.

    useEffect(() => {
        let cancelled = false;

        const raw = rawCoverUrl;
        const coverStoragePath = typeof node?.coverStoragePath === 'string'
            ? node.coverStoragePath.trim()
            : (typeof node?.storagePath === 'string' ? node.storagePath.trim() : '');

        setImageFailed(false);

        if (!raw && !coverStoragePath) {
            setResolvedCoverUrl(null);
            return undefined;
        }

        if (raw.startsWith('blob:') || raw.startsWith('data:')) {
            setResolvedCoverUrl(raw);
            return undefined;
        }

        const nodeKey = node?.key || node?.userKey || null;

        (async () => {
            try {
                const url = await resolveImageUrl({
                    nodeKey,
                    url: raw || null,
                    storagePath: coverStoragePath || null,
                });
                if (!cancelled) {
                    setResolvedCoverUrl(url || (canRenderRawCoverUrl ? raw : null));
                }
            } catch (e) {
                if (!cancelled) {
                    setResolvedCoverUrl(canRenderRawCoverUrl ? raw : null);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [canRenderRawCoverUrl, node?.coverStoragePath, node?.key, node?.storagePath, node?.userKey, rawCoverUrl]);

    // Resets image failure state when a new image source is resolved.
    useEffect(() => {
        if (resolvedCoverUrl) {
            setImageFailed(false);
        }
    }, [resolvedCoverUrl]);

    // ----- HANDLERS ----- //

    /**
     * Handles image load failures and falls back to the initial avatar.
     */

    const handleImageError = () => {
        setImageFailed(true);
    };

    /**
 * Handles click.
 *
 * @param {*} event
 */
    const handleClick = (event) => {
        if (typeof onClick === 'function') {
            onClick(event);
        }

        if (event?.defaultPrevented) {
            return;
        }

        if (!canNavigateToProfile) {
            return;
        }

        navigate(buildBaseMemberProfilePath(baseKey, userKey));
    };

    /**
 * Handles key down.
 *
 * @param {*} event
 */
    const handleKeyDown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        handleClick(event);
    };
    if (!node) return null;

    const hoverTitle = String(
        node.title
        || node.name
        || node?.user?.name
        || node?.data_profile?.name
        || '',
    ).trim();

    // ----- RENDER ----- //

    return (
        <div
          className="profile-photo-container"
          style={{
            borderColor: effectiveBorderColor || undefined,
            borderRadius: radius,
            //borderWidth: `${effectiveBorderWidth}px`,
            cursor: canNavigateToProfile || typeof onClick === 'function' ? 'pointer' : undefined,
            height: `${size}px`,
            width: `${size}px`,
          }}
          role={canNavigateToProfile || typeof onClick === 'function' ? 'button' : undefined}
          tabIndex={canNavigateToProfile || typeof onClick === 'function' ? 0 : undefined}
          title={hoverTitle || undefined}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
            {showImage ? (
                <>
                    {/* IMAGE */}
                    <img
                      className="profile-photo-image"
                      style={{
                            borderRadius: radius,
                        }}
                      alt={node.title}
                      src={imageSrc}
                      onError={handleImageError}
                    />
                </>
            ) : (
                <>
                    {/* INITIAL */}
                    <div
                      className="profile-photo-initial"
                      style={{
                        '--dot-font-size': `${initialFontSize}px`,
                        '--dot-size': `${size}px`,
                      }}
                    >
                        {initials}
                    </div>
                </>
            )}
        </div>
    );
});

// Dev Tools
ProfilePhoto.displayName = 'ProfilePhoto';

export default ProfilePhoto;
